/**
 * CommunityLayout
 *
 * Provides FloatingNav navigation for all Community module pages.
 * Each page wraps its content in this component (inside their own AppLayout).
 *
 * Usage:
 *   <AppLayout>
 *     <CommunityLayout>
 *       <PageLayout title="People & Households">...</PageLayout>
 *     </CommunityLayout>
 *   </AppLayout>
 */

import { useMemo, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PeopleSubNavHost } from "./PeopleSubNavHost";
import { FloatingNav, type FloatingNavCategory, type FloatingNavItem } from "@/components/layout/FloatingNav";
import { getModuleAccent } from "@/lib/module-accents";
import {
  PRPeopleGroup,
  PRPeopleDirectory,
  PRPeopleMerge,
  PRPeopleContact,
  PRPeopleEngagement,
  PRPeopleEngagementScore,
  PRPeopleVisitor,
  PRPeopleLifeEvents,
  PRPeoplePathways,
} from "@/components/icons/PhotoRealCommunityIcons";
import { PRGroupSearch } from "@/components/icons/PhotoRealMinistryIcons";
import { usePermissions } from "@/hooks/usePermissions";
import { useUiUplift } from "@/hooks/useUiUplift";
import { GROUP_MANAGEMENT_PATH } from "@/lib/groups-placement";
import { CARE_PATH } from "@/lib/care-placement";

const communityAccent = getModuleAccent("people");

const COMMUNITY_CATEGORIES: FloatingNavCategory[] = [
  {
    group: "People",
    groupIcon: PRPeopleGroup,
    items: [
      {
        id: "directory",
        label: "People & Households",
        icon: PRPeopleDirectory,
        description: "Community directory",
      },
      {
        id: "organizations",
        label: "Organizations",
        icon: PRPeopleGroup,
        description: "Companies, vendors & business donors",
      },
      {
        id: "merge",
        label: "Merge duplicates",
        icon: PRPeopleMerge,
        description: "Merge duplicate records",
      },
      {
        id: "registration-approvals",
        label: "Registration approvals",
        icon: PRPeopleVisitor,
        description: "Review self-registration requests",
      },
      {
        id: "member-directory",
        label: "Member directory",
        icon: PRPeopleContact,
        description: "Member directory",
      },
    ],
  },
  {
    group: "Engagement",
    groupIcon: PRPeopleEngagement,
    items: [
      {
        id: "engagement",
        label: "Engagement scoring",
        icon: PRPeopleEngagementScore,
        description: "Engagement metrics",
      },
      {
        id: "visitor-followup",
        label: "Visitor follow-up",
        icon: PRPeopleVisitor,
        description: "Follow up with visitors",
      },
      {
        id: "life-events",
        label: "Life events",
        icon: PRPeopleLifeEvents,
        description: "Life event tracking",
      },
      {
        id: "pathways",
        label: "Membership pathways",
        icon: PRPeoplePathways,
        description: "Membership journey",
      },
    ],
  },
];

/**
 * Map the current pathname to a section id.
 */
function sectionIdFromPath(pathname: string): string {
  const path = pathname.toLowerCase();

  if (path === "/community" || path === "/people" || path === "/people/all" || path.startsWith("/community/dashboard")) return "directory";
  if (path.startsWith("/community/organizations")) return "organizations";
  if (path.startsWith("/community/merge")) return "merge";
  if (path.startsWith("/community/registration-approvals")) return "registration-approvals";
  if (path.startsWith("/community/directory")) return "member-directory";
  if (path.startsWith("/engagement")) return "engagement";
  if (path.startsWith("/people/visitor-followup")) return "visitor-followup";
  if (path.startsWith("/people/life-events")) return "life-events";
  if (path.startsWith("/people/membership-pathways")) return "pathways";
  if (path.startsWith(GROUP_MANAGEMENT_PATH)) return "groups";
  if (path === CARE_PATH || path.startsWith(`${CARE_PATH}/`)) return "care";

  return "directory";
}

function pathFromSectionId(sectionId: string): string {
  switch (sectionId) {
    case "directory": return "/people/all";
    case "organizations": return "/community/organizations";
    case "merge": return "/community/merge";
    case "registration-approvals": return "/community/registration-approvals";
    case "member-directory": return "/community/directory";
    case "engagement": return "/engagement";
    case "visitor-followup": return "/people/visitor-followup";
    case "life-events": return "/people/life-events";
    case "pathways": return "/people/membership-pathways";
    case "groups": return GROUP_MANAGEMENT_PATH;
    case "care": return CARE_PATH;
    default: return "/people/all";
  }
}

interface CommunityLayoutProps {
  children: ReactNode;
}

/** Under ui_uplift = next, the group list is part of People (MODULE_MAPS.md: People › Groups). */
const GROUPS_ITEM = {
  id: "groups",
  label: "Groups",
  icon: PRGroupSearch,
  description: "Small groups and classes",
} as FloatingNavItem;

/** Under ui_uplift = next, pastoral care is part of People (MODULE_MAPS.md: People › Care). */
const CARE_ITEM = {
  id: "care",
  label: "Care",
  icon: PRPeopleEngagement,
  description: "Pastoral care requests",
} as FloatingNavItem;

export function CommunityLayout({ children }: CommunityLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  // Group management is an org-admin page (its route is behind OrgAdminRoute), so only admins
  // get the item.
  const { isOrgAdmin, isLoading: permissionsLoading, canAccessModule } = usePermissions();
  const isNext = useUiUplift() === "next";
  const withGroups = isNext && isOrgAdmin && !permissionsLoading;
  // Care's route only needs a signed-in member; which requests show is up to RLS.
  const withCare = isNext;
  // Someone without the People module (an AP clerk, say) can still reach Care; they get Care
  // alone, not the People pages their sidebar hides. On Care, that's also the menu while access
  // loads, so it never briefly offers those pages; elsewhere in People the full menu stays.
  const onCare = sectionIdFromPath(location.pathname) === "care";
  const careOnly = withCare && (permissionsLoading ? onCare : !canAccessModule("people"));
  const categories = useMemo(
    () => {
      if (careOnly) return [{ ...COMMUNITY_CATEGORIES[0], items: [CARE_ITEM] }];
      const extra = [...(withGroups ? [GROUPS_ITEM] : []), ...(withCare ? [CARE_ITEM] : [])];
      return extra.length > 0
        ? COMMUNITY_CATEGORIES.map((category, index) =>
            index === 0 ? { ...category, items: [...category.items, ...extra] } : category,
          )
        : COMMUNITY_CATEGORIES;
    },
    [careOnly, withGroups, withCare],
  );
  // Carry a ?ui= preview choice across the sub-nav: where storage can't keep it, the URL is
  // all that holds it, and dropping it would switch the next page back to the classic layout.
  const uiParam = new URLSearchParams(location.search).get("ui");
  const uiSearch = uiParam === "next" || uiParam === "classic" ? `?ui=${uiParam}` : "";

  const activeSection = useMemo(() => {
    const id = sectionIdFromPath(location.pathname);
    const allItems = categories.flatMap((cat) => cat.items);
    return allItems.some((item) => item.id === id) ? id : allItems[0]?.id ?? "directory";
  }, [location.pathname, categories]);

  return (
    // The module sub-nav (#6678) under ui_uplift = next; see PeopleSubNavHost. It replaces the
    // legacy section menu where it shows, so a page never carries two section navs.
    <PeopleSubNavHost>
      {(barShown) => (
        <>
          {barShown ? null : (
            <FloatingNav
              categories={categories}
              activeSection={activeSection}
              onNavigate={(id) => navigate(`${pathFromSectionId(id)}${uiSearch}`)}
              moduleAccent={communityAccent.color}
            />
          )}
          {children}
        </>
      )}
    </PeopleSubNavHost>
  );
}
